import { FC, Key, MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";

interface AutocompleteProps {
  label: string;
  options: { [key: string]: string };
  value: string;
  onChange: (newValue: string) => void;
  placeholder?: string;
  /** Accept any typed text as the value, using the options only as suggestions. */
  allowCustomValue?: boolean;
}

export const Autocomplete: FC<AutocompleteProps> = (props) => {
  const [search, setSearch] = useState("");
  const [hasFocus, setHasFocus] = useState(false);

  const input = useRef<HTMLInputElement>(null);

  // With custom values the typed text is the value itself, so it doubles as the search term.
  const searchTerm = props.allowCustomValue ? props.value : search;
  // With custom values nothing is preselected, so Enter keeps the typed text.
  const initialPosition = props.allowCustomValue ? null : 0;

  const selectedOptionText = useMemo(() => {
    return props.options[props.value];
  }, [props.options, props.value]);

  const options = useMemo(
    () =>
      Object.entries(props.options).filter(([, value]) => {
        return value.toLowerCase().includes(searchTerm.toLowerCase());
      }),
    [props.options, searchTerm],
  );

  const selectOption = (key: string) => {
    setSearch(props.options[key]);
    props.onChange(key);
    setHasFocus(false);
    input.current?.blur();
  };

  const [selectedPosition, setSelectedPosition] = useState<number | null>(initialPosition);

  useEffect(() => {
    if (selectedPosition !== null && !options[selectedPosition]) {
      setSelectedPosition(initialPosition);
    }
  }, [selectedPosition, options]);

  const onItemClick = (event: MouseEvent<HTMLButtonElement>, key: string) => {
    event.preventDefault();
    selectOption(key);
  };
  const onInputFocus = () => {
    setHasFocus(true);
    setSearch("");
  };
  const onInputBlur = () => {
    setHasFocus(false);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedPosition(Math.min((selectedPosition ?? -1) + 1, options.length - 1));
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedPosition(selectedPosition === null || selectedPosition === 0 ? initialPosition : selectedPosition - 1);
    }
    if (event.key === "Escape") {
      input.current?.blur();
    }
    const highlighted = selectedPosition !== null ? options[selectedPosition] : undefined;
    if ((event.key === "Enter" || event.key === "Tab") && highlighted) {
      event.preventDefault();
      selectOption(highlighted[0]);
    }
  };

  return (
    <label className={"flex flex-col text-sm font-medium text-brand-900 w-full max-w-md"}>
      {props.label}
      <div className={"relative mt-1"}>
        <input
          ref={input}
          onFocus={() => onInputFocus()}
          onBlur={() => onInputBlur()}
          onKeyDown={(e) => onInputKeyDown(e)}
          className={
            "border-2 border-slate-200 outline-brand-700 text-black rounded-sm text-base font-normal px-2 h-10 w-full"
          }
          placeholder={props.placeholder}
          onChange={(e) => (props.allowCustomValue ? props.onChange(e.target.value) : setSearch(e.target.value))}
          value={props.allowCustomValue ? props.value : hasFocus ? search : selectedOptionText}
        />

        {/* Chevron icon*/}
        <div className={"absolute top-0 right-2 h-full flex items-center justify-end"}>
          <FontAwesomeIcon icon={faChevronDown} className={"text-xs text-brand-900"} />
        </div>

        {hasFocus && options.length > 0 && (
          <div
            className={
              "absolute top-10 left-0 w-full text-black bg-white rounded-sm shadow-sm z-10 max-h-[220px] overflow-y-scroll"
            }
          >
            <div className={"flex flex-col items-stretch"}>
              {options.map(([key, value], index) => {
                return (
                  <button
                    key={index}
                    className={`h-10 w-full px-4 ${index === selectedPosition ? "bg-slate-100" : ""} hover:bg-slate-100 ${key === props.value ? "text-brand-800" : ""} text-left`}
                    onMouseDown={(e) => onItemClick(e, key)}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </label>
  );
};

export type KeyProperties<T> = {
  [P in keyof T]: T[P] extends Key ? P : never;
}[keyof T];

interface ObjectAutocompleteProps<T> {
  label: string;
  options: T[];
  value: T | null;
  onChange: (newValue: T) => void;
  displayItem: (obj: T) => string;
  idProperty: KeyProperties<T>;
}
export const ObjectAutocomplete = function <T>(props: ObjectAutocompleteProps<T>): ReactNode {
  const getDisplayValue = (option: T) => {
    return props.displayItem(option);
  };
  const getKey = (option: T) => {
    return option[props.idProperty];
  };

  const options = useMemo(() => {
    return props.options.reduce<AutocompleteProps["options"]>((map, option) => {
      const key = getKey(option);
      // @ts-expect-error key's type is too broad for the options record's index signature
      map[key] = getDisplayValue(option);
      return map;
    }, {});
  }, [props.options]);

  const onChange = (newKey: string) => {
    const value = props.options.find((option) => getKey(option) === newKey);
    if (value) {
      props.onChange(value);
    }
  };
  if (!props.value) {
    return <AutocompletePlaceholder label={props.label} />;
  }
  return (
    <Autocomplete label={props.label} options={options} value={getKey(props.value) as string} onChange={onChange} />
  );
};

const AutocompletePlaceholder: FC<{ label: string }> = (props) => {
  return (
    <div className={"flex flex-col text-sm font-medium text-brand-900 w-full max-w-md"}>
      {props.label}
      <div className={"relative mt-1"}>
        <div
          className={
            "border-2 border-slate-200 outline-brand-700 text-black rounded-sm text-base font-normal px-2 h-10 w-full"
          }
        />
        {/* Chevron icon*/}
        <div className={"absolute top-0 right-2 h-full flex items-center justify-end"}>
          <FontAwesomeIcon icon={faChevronDown} className={"text-xs text-brand-900"} />
        </div>
      </div>
    </div>
  );
};
